import React from "react";
import { Box, Grid, Chip } from "@mui/material";
import {
  LocalShippingOutlined,
  ManageAccountsOutlined,
  DateRangeOutlined,
  InfoOutlined,
  DirectionsCarOutlined,
  PersonOutlined,
} from "@mui/icons-material";
import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import { localizeDateTimeShort } from "utils/dateFormats";
// import { useAuth } from "hooks";
import { useLocalTransportDetailContext } from "contexts/LocalTransportDetailContext";

const statusStyles = {
  Pending: { backgroundColor: "#FFF3CD", textColor: "#856404" },
  "Auto Approved": { backgroundColor: "#D4EDDA", textColor: "#155724" },
  "Manual Approved": { backgroundColor: "#D1ECF1", textColor: "#0C5460" },
  Rejected: { backgroundColor: "#F8D7DA", textColor: "#721C24" },
  default: { backgroundColor: "#E9ECEF", textColor: "#6C757D" },
};

const getStatusStyle = (status) => statusStyles[status] || statusStyles.default;

const LocalTransportBasicDetail = () => {
  // const { isCustomer, isVendor, isContractor, isDriver } = useAuth();
  const { localTransportDetail, loadingLocalTransport } =
    useLocalTransportDetailContext();

  return !loadingLocalTransport ? (
    <Box padding={3}>
      <Grid container spacing={4}>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <LocalShippingOutlined color="primary" />}
            title="LT ID"
            subtitle={
              localTransportDetail?.ltTag ||
              `LT-${localTransportDetail?._id?.slice(-6).toUpperCase()}`
            }
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <ManageAccountsOutlined color="primary" />}
            title="Branch"
            subtitle={localTransportDetail?.branch?.name || "N/A"}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <ManageAccountsOutlined color="primary" />}
            title="To Branch"
            subtitle={localTransportDetail?.toBranch?.name || "N/A"}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="No Of Sacks"
            subtitle={localTransportDetail?.noOfSacks || "N/A"}
          />
        </Grid>
        {localTransportDetail?.contractorId && (
          <Grid item xs={3}>
            <InformationTile
              icon={() => <PersonOutlined color="primary" />}
              title="Contractor"
              subtitle={
                <Box>
                  <Box fontWeight="bold">
                    {localTransportDetail.contractorId.name || "N/A"}
                  </Box>
                  <Box fontSize="0.875rem" color="text.secondary">
                    ID: {localTransportDetail.contractorId.contractorId || "N/A"}
                  </Box>
                </Box>
              }
            />
          </Grid>
        )}

        {localTransportDetail?.vehicleId && (
          <Grid item xs={3}>
            <InformationTile
              icon={() => <DirectionsCarOutlined color="primary" />}
              title="Vehicle"
              subtitle={
                <Box>
                  <Box fontWeight="bold">
                    {localTransportDetail.vehicleId.vehicle_id || "N/A"}
                  </Box>
                  <Box fontSize="0.875rem" color="text.secondary">
                    Reg: {localTransportDetail.vehicleId.registration_number || "N/A"}
                  </Box>
                </Box>
              }
            />
          </Grid>
        )}
        <Grid item xs={3}>
          <InformationTile
            icon={() => <ManageAccountsOutlined color="primary" />}
            title="Mode"
            subtitle={localTransportDetail?.transportationMode?.name || "N/A"}
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Distance (km)"
            subtitle={localTransportDetail?.distanceKm || "N/A"}
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Actual Cost"
            subtitle={localTransportDetail?.estimatedCost || "N/A"}
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <ManageAccountsOutlined color="primary" />}
            title="Requested By"
            subtitle={localTransportDetail?.requestBy?.name || "N/A"}
          />
        </Grid>

        {
          localTransportDetail?.approver && (
            <Grid item xs={3}>
              <InformationTile
                icon={() => <ManageAccountsOutlined color="primary" />}
                title="Approver"
                subtitle={localTransportDetail.approver.name}
              />
            </Grid>
          )}

        <Grid item xs={3}>
          <InformationTile
            icon={() => <ManageAccountsOutlined color="primary" />}
            title={
              localTransportDetail?.status === "Rejected"
                ? "Rejected By"
                : "Approved By"
            }
            subtitle={localTransportDetail?.approvedBy?.name || "N/A"}
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <DateRangeOutlined color="primary" />}
            title={
              localTransportDetail?.status === "Rejected"
                ? "Rejected At"
                : "Approved At"
            }
            subtitle={
              localTransportDetail?.approvedAt
                ? localizeDateTimeShort(localTransportDetail.approvedAt)
                : "N/A"
            }
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <DateRangeOutlined color="primary" />}
            title="Created At"
            subtitle={
              localTransportDetail?.createdAt
                ? localizeDateTimeShort(localTransportDetail.createdAt)
                : "N/A"
            }
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Status"
            subtitle={
              <Box
                display="flex"
                flexDirection="column"
                alignItems="start"
                mt={1}
              >
                <Chip
                  label={localTransportDetail?.status || "Unknown"}
                  style={{
                    backgroundColor: getStatusStyle(
                      localTransportDetail?.status
                    ).backgroundColor,
                    color: getStatusStyle(localTransportDetail?.status)
                      .textColor,
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "0.7rem",
                    height: "20px",
                    padding: "0 6px",
                    borderRadius: "12px",
                    marginTop: "4px",
                  }}
                />
              </Box>
            }
          />
        </Grid>

        <Grid item xs={6}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="System Remarks"
            subtitle={localTransportDetail?.systemRemarks || "N/A"}
          />
        </Grid>
        <Grid item xs={12}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Remarks"
            subtitle={localTransportDetail?.remarks || "N/A"}
          />
        </Grid>
      </Grid>
    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default LocalTransportBasicDetail;
